/**
 * Global Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree, logs errors,
 * and displays a fallback UI instead of crashing the entire app.
 *
 * Features:
 * - Catches all React component errors
 * - User-friendly error messages
 * - Recovery options (reload, clear cache)
 * - Error logging for debugging
 * - Automatic error reporting (optional)
 */

import { Component, ReactNode, ErrorInfo } from 'react'

/**
 * Send error to backend API for Sentry reporting
 * This allows the client to report errors without exposing Sentry DSN
 */
async function reportErrorToBackend(error: Error, errorInfo: ErrorInfo): Promise<void> {
  try {
    await fetch('/api/error-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error: {
          message: error.message,
          name: error.name,
          stack: error.stack,
        },
        componentStack: errorInfo.componentStack,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      }),
    })
  } catch (reportError) {
    // Silently fail - don't let error reporting crash the app
    console.error('Failed to report error:', reportError)
  }
}

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional custom fallback UI */
  fallback?: (error: Error, errorInfo: ErrorInfo) => ReactNode
  /** Optional error reporting callback */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('🚨 Error Boundary caught an error:', error)
      console.error('Component stack:', errorInfo.componentStack)
    }

    // Call optional error reporting callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Update state with error info
    this.setState({
      errorInfo,
    })

    // Send error to monitoring service (Sentry via backend API)
    reportErrorToBackend(error, errorInfo).catch((err) => {
      // Error reporting failed, but don't block the UI
      console.error('Error reporting failed:', err)
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleClearCache = () => {
    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.clear()
      sessionStorage.clear()

      // Show feedback
      alert('Cache cleared. The page will now reload.')

      // Reload page
      window.location.reload()
    }
  }

  handleReset = () => {
    // Reset error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo!)
      }

      // Default fallback UI
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-background, #ffffff)',
          padding: 'var(--spacing-lg, 1.5rem)',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            padding: 'var(--spacing-2xl, 3rem)',
            backgroundColor: 'var(--color-surface, #f5f5f5)',
            borderRadius: 'var(--radius-lg, 0.5rem)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}>
            {/* Error Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto var(--spacing-lg, 1.5rem)',
              backgroundColor: 'var(--color-error, #ef4444)',
              borderRadius: 'var(--radius-full, 9999px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontSize: '32px',
              fontWeight: 'bold',
            }}>
              !
            </div>

            {/* Error Title */}
            <h1 style={{
              margin: '0 0 var(--spacing-md, 1rem)',
              fontSize: 'var(--font-size-2xl, 1.5rem)',
              fontWeight: 'var(--font-weight-bold, 700)',
              color: 'var(--color-text-primary, #1a1a1a)',
              textAlign: 'center',
            }}>
              Something went wrong
            </h1>

            {/* Error Message */}
            <p style={{
              margin: '0 0 var(--spacing-lg, 1.5rem)',
              fontSize: 'var(--font-size-base, 1rem)',
              color: 'var(--color-text-secondary, #6b7280)',
              textAlign: 'center',
              lineHeight: 'var(--line-height-relaxed, 1.75)',
            }}>
              We encountered an unexpected error. Don't worry, your data is safe.
              Try one of the options below to continue.
            </p>

            {/* Error Details (Development Only) */}
            {import.meta.env.DEV && (
              <details style={{
                margin: '0 0 var(--spacing-lg, 1.5rem)',
                padding: 'var(--spacing-md, 1rem)',
                backgroundColor: '#f9fafb',
                borderRadius: 'var(--radius-md, 0.375rem)',
                border: '1px solid #e5e7eb',
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm, 0.875rem)',
                  fontWeight: 'var(--font-weight-medium, 500)',
                  color: 'var(--color-text-primary, #1a1a1a)',
                  marginBottom: 'var(--spacing-sm, 0.5rem)',
                }}>
                  Error Details (Development Mode)
                </summary>
                <pre style={{
                  margin: 'var(--spacing-sm, 0.5rem) 0 0',
                  padding: 'var(--spacing-sm, 0.5rem)',
                  fontSize: 'var(--font-size-xs, 0.75rem)',
                  fontFamily: 'var(--font-mono, monospace)',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 'var(--radius-sm, 0.125rem)',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack && `\n\nComponent Stack:${this.state.errorInfo.componentStack}`}
                </pre>
              </details>
            )}

            {/* Recovery Actions */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-sm, 0.5rem)',
            }}>
              {/* Reload Button */}
              <button
                onClick={this.handleReload}
                style={{
                  padding: 'var(--spacing-md, 1rem) var(--spacing-lg, 1.5rem)',
                  fontSize: 'var(--font-size-base, 1rem)',
                  fontWeight: 'var(--font-weight-medium, 500)',
                  color: '#ffffff',
                  backgroundColor: 'var(--color-primary, #1a1a1a)',
                  border: 'none',
                  borderRadius: 'var(--radius-md, 0.375rem)',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = '0.9'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = '1'
                }}
              >
                Reload Page
              </button>

              {/* Try Again Button */}
              <button
                onClick={this.handleReset}
                style={{
                  padding: 'var(--spacing-md, 1rem) var(--spacing-lg, 1.5rem)',
                  fontSize: 'var(--font-size-base, 1rem)',
                  fontWeight: 'var(--font-weight-medium, 500)',
                  color: 'var(--color-text-primary, #1a1a1a)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-text-primary, #1a1a1a)',
                  borderRadius: 'var(--radius-md, 0.375rem)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                Try Again
              </button>

              {/* Clear Cache Button */}
              <button
                onClick={this.handleClearCache}
                style={{
                  padding: 'var(--spacing-sm, 0.5rem)',
                  fontSize: 'var(--font-size-sm, 0.875rem)',
                  fontWeight: 'var(--font-weight-normal, 400)',
                  color: 'var(--color-text-secondary, #6b7280)',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = 'var(--color-text-primary, #1a1a1a)'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = 'var(--color-text-secondary, #6b7280)'
                }}
              >
                Clear cache and reload
              </button>
            </div>

            {/* Support Message */}
            <p style={{
              margin: 'var(--spacing-lg, 1.5rem) 0 0',
              fontSize: 'var(--font-size-sm, 0.875rem)',
              color: 'var(--color-text-secondary, #6b7280)',
              textAlign: 'center',
            }}>
              If the problem persists, please{' '}
              <a
                href="https://github.com/a2m2c/agustinmusi-com/issues"
                style={{
                  color: 'var(--color-primary, #1a1a1a)',
                  textDecoration: 'underline',
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                report this issue
              </a>
              .
            </p>
          </div>
        </div>
      )
    }

    // No error - render children normally
    return this.props.children
  }
}
